// What a binned histogram can honestly say about a SINGLE-VALUE operator (ACT2-534).
//
// `=` and `≠` ask "how many rows hold exactly this value". A histogram cannot answer that: the bucket
// is the finest resolution the measurement has. The shipped behaviour filled the whole bucket in the
// accent colour and quoted its entire count as the match — on a date column binned ~80 days wide, an
// `= Oct 9, 1970` rule reported every row created that quarter, overstating the real answer by roughly
// the bucket width in days.
//
// So we state a BOUND instead of inventing a number: `=` can match at most the bucket, `≠` at least
// everything outside it. The bucket is drawn muted (it CONTAINS the answer) while the value itself gets
// a hard marker, so the picture and the sentence agree about what is known.

import type { DistributionBin } from '../types';
import type { Span } from './slider-ops';

export interface PointOperatorBound {
  /** `upper` ⇒ "Up to N"; `lower` ⇒ "At least N". ABSENT ⇒ the figure is EXACT and needs no hedge — a
   *  bucket no wider than the value's own granularity holds one distinct value, so its count is the
   *  answer rather than a ceiling over it. */
  bound?: 'upper' | 'lower';
  /** The bound itself. ABSENT ⇒ not knowable — the readout goes quiet rather than printing a number. */
  count?: number;
  /** The bucket that merely contains the value — drawn muted. */
  soft: Span[];
  /** Rows that definitely match: nothing for `=`, everything outside the bucket for `≠`. */
  segments: Span[];
  note: string;
}

/**
 * Bound, spans and disclosure for `eq` / `neq` against the bucket holding the value.
 *
 * `bin` ABSENT means the value falls outside the charted frame. `=` then has no bucket to bound it and
 * withholds the count; `≠` is bounded below by everything charted, since no charted row can equal a
 * value the chart does not cover.
 */
export function pointOperatorBound(args: {
  op: 'eq' | 'neq';
  bin: DistributionBin | undefined;
  /** Rows in the whole population, when known — includes any that fall outside the charted frame. */
  total?: number;
  /** Rows across the charted bins. Used as the denominator when `total` is unknown. */
  binsTotal: number;
  /** The smallest step the value can move by. A bucket no wider than this holds ONE distinct value, so
   *  its count is exact rather than a ceiling. Absent ⇒ unknown, and everything stays a bound. */
  grain?: number;
  pct: (v: number) => number;
  format: (n: number) => string;
}): PointOperatorBound {
  const { bin, binsTotal, format, grain, op, pct, total } = args;

  if (!bin) {
    return op === 'eq'
      ? { bound: 'upper', soft: [], segments: [], note: 'The value sits outside the charted range.' }
      : {
          bound: 'lower',
          // CHARTED rows only. `total` counts the out-of-frame rows too, and the value sits in exactly
          // the region those occupy — so not one of them is PROVEN to differ from it. Claiming them
          // errs in the unsafe direction: a sentinel column (`age = -1` for unknown, the very case the
          // p01/p99 framing exists for) would report the whole population as differing.
          count: binsTotal,
          soft: [],
          segments: [[0, 100]],
          note: 'The value sits outside the charted range, so every charted row differs from it.',
        };
  }

  // A bucket at or below the value's own granularity cannot hold a second distinct value, so there is
  // nothing to bound — the count IS the answer, and the bucket IS the match. Hedging here would be the
  // same defect as the one this module fixes, pointed the other way.
  const exact = typeof grain === 'number' && grain > 0 && bin.end - bin.start <= grain;

  const span: Span = [pct(bin.start), pct(bin.end)];
  const bucket = `${format(bin.start)}–${format(bin.end)}`;
  if (op === 'eq') {
    return exact
      ? { count: bin.count, soft: [], segments: [span], note: '' }
      : {
          bound: 'upper',
          count: bin.count,
          soft: [span],
          segments: [],
          note: `A histogram can't resolve a single value — this is the whole ${bucket} bucket.`,
        };
  }
  if (exact) {
    return {
      count: Math.max(0, (total ?? binsTotal) - bin.count),
      soft: [],
      segments: [
        [0, span[0]],
        [span[1], 100],
      ],
      note: '',
    };
  }
  return {
    bound: 'lower',
    // Everything outside the bucket differs from the value — including the out-of-frame rows, which sit
    // beyond the charted extremes and so cannot equal a value inside them. Hence `total`, not `binsTotal`.
    count: Math.max(0, (total ?? binsTotal) - bin.count),
    soft: [span],
    segments: [
      [0, span[0]],
      [span[1], 100],
    ],
    note: `A histogram can't resolve a single value — the ${bucket} bucket is excluded whole.`,
  };
}
